// src/components/Admin/Menu_Management/CategoryAddEditForm.jsx
import React, { useState, useEffect } from "react";
import { X, FolderOpen, Info, Check } from "lucide-react";
import useTax from "../../../hooks/useTax";

export default function CategoryAddEditForm({
  item,
  onClose,
  onSave,
  serverError = null,
  onClearServerError,
  existingCategories = [],
}) {
  const { taxes } = useTax();
  const activeTaxes = taxes.filter((t) => t.isActive);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    displayOrder: "",
    taxId: null,
    inclusiveTaxRate: true,
    isActive: true,
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Initialize form with item data
  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || "",
        description: item.description || "",
        displayOrder: item.displayOrder?.toString() || "",
        taxId: item.taxId || null,
        inclusiveTaxRate: item.inclusiveTaxRate !== false,
        isActive: item.isActive !== undefined ? item.isActive : true,
      });
    }
  }, [item]);

  // A server-side field error (e.g. duplicate name) arrives after the save attempt:
  // show it on the field and put the cursor back in it.
  useEffect(() => {
    if (!serverError?.field) return;

    setErrors((prev) => ({ ...prev, [serverError.field]: serverError.message }));

    const timer = setTimeout(() => {
      const el = document.querySelector(`[data-field="${serverError.field}"]`);
      el?.focus();
      el?.select?.();
    }, 50);
    return () => clearTimeout(timer);
  }, [serverError]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error when field changes
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
      // Let the parent drop the server error too, so it can't be re-applied.
      onClearServerError?.();
    }
  };

  const validate = () => {
    const newErrors = {};
    const trimmedName = formData.name.trim();

    if (!trimmedName) {
      newErrors.name = "Category name is required";
    } else {
      // Catch a duplicate before the round-trip. Only fires on an exact
      // case-insensitive hit in the loaded list (excluding the category being
      // edited), so it cannot false-positive; the API still rejects what it misses.
      const duplicate = existingCategories.find(
        (existing) =>
          existing?.name?.trim().toLowerCase() === trimmedName.toLowerCase() &&
          existing.id !== item?.id
      );
      if (duplicate) {
        newErrors.name = `A category named "${duplicate.name}" already exists. Please choose a different name.`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        displayOrder: formData.displayOrder ? parseInt(formData.displayOrder) : null,
        taxId: formData.taxId || null,
        inclusiveTaxRate: formData.inclusiveTaxRate,
        isActive: formData.isActive,
      };
      await onSave(payload);
    } finally {
      setSaving(false);
    }
  };

  // Get selected tax details for display
  const selectedTax = formData.taxId
    ? activeTaxes.find((t) => t.id === formData.taxId)
    : null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <FolderOpen className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">
                  {item ? "Edit Category" : "Add Category"}
                </h2>
                <p className="text-teal-100 text-xs">
                  {item ? "Update category details" : "Create a new menu category"}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Basic Info Section */}
          <div className="space-y-4">
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              Basic Information
            </h3>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Category Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                data-field="name"
                value={formData.name}
                onChange={(e) => handleChange("name", e.target.value)}
                placeholder="e.g., Starters, Main Course, Beverages"
                aria-invalid={!!errors.name}
                className={`w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all ${
                  errors.name
                    ? "border-red-300 bg-red-50"
                    : "border-gray-300 bg-white"
                }`}
              />
              {errors.name && (
                <p className="text-xs text-red-500 mt-1">{errors.name}</p>
              )}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="Brief description of this category (optional)"
                rows={2}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white resize-none"
              />
            </div>

            {/* Display Order & Status */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Display Order
                </label>
                <input
                  type="number"
                  value={formData.displayOrder}
                  onChange={(e) => handleChange("displayOrder", e.target.value)}
                  placeholder="e.g., 1, 2, 3..."
                  min="1"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Status
                </label>
                <div className="flex items-center h-[42px]">
                  <button
                    type="button"
                    onClick={() => handleChange("isActive", !formData.isActive)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${
                      formData.isActive ? "bg-teal-600" : "bg-gray-200"
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                        formData.isActive ? "translate-x-5" : "translate-x-0"
                      }`}
                    />
                  </button>
                  <span
                    className={`ml-3 text-sm font-medium ${
                      formData.isActive ? "text-teal-700" : "text-gray-500"
                    }`}
                  >
                    {formData.isActive ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Tax Configuration Section */}
          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                Tax Configuration
              </h3>
            </div>

            {/* Info Banner */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
              <Info size={14} className="text-blue-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-700">
                Menu items in this category will inherit this tax unless overridden at the item level.
              </p>
            </div>

            {/* Tax Dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Tax Rate
              </label>
              <select
                value={formData.taxId || ""}
                onChange={(e) =>
                  handleChange("taxId", e.target.value ? Number(e.target.value) : null)
                }
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white"
              >
                <option value="">-- No Tax (Use Default) --</option>
                {activeTaxes.map((tax) => (
                  <option key={tax.id} value={tax.id}>
                    {tax.name} ({tax.rate}%)
                    {tax.centerGstRate && tax.stateGstRate
                      ? ` [CGST: ${tax.centerGstRate}% + SGST: ${tax.stateGstRate}%]`
                      : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* Selected Tax Details */}
            {selectedTax && (
              <div className="p-3 bg-teal-50 border border-teal-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-teal-800">
                      {selectedTax.name}
                    </p>
                    <p className="text-xs text-teal-600 mt-0.5">
                      Total Rate: {selectedTax.rate}%
                      {selectedTax.centerGstRate && selectedTax.stateGstRate && (
                        <span className="ml-2">
                          (CGST: {selectedTax.centerGstRate}% + SGST: {selectedTax.stateGstRate}%)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Tax Inclusive Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div>
                <p className="text-sm font-medium text-gray-700">Tax Inclusive Pricing</p>
                <p className="text-xs text-gray-500 mt-0.5">
                  Menu prices already include tax
                </p>
              </div>
              <button
                type="button"
                onClick={() => handleChange("inclusiveTaxRate", !formData.inclusiveTaxRate)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${
                  formData.inclusiveTaxRate ? "bg-teal-600" : "bg-gray-200"
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    formData.inclusiveTaxRate ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check size={16} />
                  {item ? "Update Category" : "Create Category"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
